import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { ConfluenceSourceSettingsApi } from "./ConfluenceSourceSettings.Api";
import type { ConfluenceSourceSettingsViewModel } from "./ConfluenceSourceSettings.ViewModel.types";
import type {
  ConfluenceConfiguration,
  ConfluenceResource,
  ConfluenceSpace,
} from "./ConfluenceSourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

export const useConfluenceSourceSettingsViewModel = (
  model: SourceSettingsModel,
): ConfluenceSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedResourceForConnectionRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [resources, setResources] = useState<ConfluenceResource[]>([]);
  const [spaces, setSpaces] = useState<ConfluenceSpace[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Confluence && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<ConfluenceConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );
  const selectedResourceId = normalizeId(configuration.resource?.id);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedResourceForConnectionRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<ConfluenceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        spaces: cfg.spaces ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity =
      `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setSpaces([]);

    updateConnectionConfiguration<ConfluenceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        spaces: [],
        issueTypes: [],
        doneStatus: undefined,
        statusesToLoad: [],
        resource: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadResources = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setResources([]);
        setResourcesLoaded(true);
        return;
      }

      const confluenceApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ConfluenceSourceSettingsApi>(
          SourceTypeIds.Confluence,
        );
      if (!confluenceApi) {
        setResources([]);
        setResourcesLoaded(true);
        return;
      }

      setResourcesLoaded(false);
      setHasError(false);
      try {
        const nextResources = await confluenceApi.getResources(
          model.boardId,
          editingConnectionId,
        );
        setResources(nextResources);

        if (
          !selectedResourceId &&
          nextResources.length > 0 &&
          autoSelectedResourceForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedResourceForConnectionRef.current = editingConnectionId;
          updateConnectionConfiguration<ConfluenceConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.resource?.id)
                ? cfg
                : { ...cfg, resource: nextResources[0], spaces: [] },
            { isDefault: true },
          );
        }
      } catch {
        setResources([]);
        setHasError(true);
      } finally {
        setResourcesLoaded(true);
      }
    };

    void loadResources();
  }, [editingConnectionId, isVisible, model.boardId, selectedResourceId]);

  useEffect(() => {
    const loadSpaces = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedResourceId) {
        setSpaces([]);
        setSpacesLoaded(true);
        return;
      }

      const confluenceApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ConfluenceSourceSettingsApi>(
          SourceTypeIds.Confluence,
        );
      if (!confluenceApi) {
        setSpaces([]);
        setSpacesLoaded(true);
        return;
      }

      setSpacesLoaded(false);
      setHasError(false);
      try {
        setSpaces(
          await confluenceApi.getSpaces(
            model.boardId,
            editingConnectionId,
            selectedResourceId,
          ),
        );
      } catch {
        setSpaces([]);
        setHasError(true);
      } finally {
        setSpacesLoaded(true);
      }
    };

    void loadSpaces();
  }, [editingConnectionId, isVisible, model.boardId, selectedResourceId]);

  return {
    isVisible,
    resources,
    spaces,
    resourcesLoaded,
    spacesLoaded,
    hasError,
    selectedResourceId: selectedResourceId || NONE_OPTION,
    selectedResourceName:
      selectedResourceId === NONE_OPTION
        ? ""
        : resources.find((resource) => normalizeId(resource.id) === selectedResourceId)
            ?.name || "",
    selectedSpaceIds: (configuration.spaces ?? []).map((space) => normalizeId(space.id)),
    assignedToMe: configuration.assignedToMe ?? false,
    noneOptionValue: NONE_OPTION,
    onSelectResource: (resourceId?: string) => {
      const selectedId = String(resourceId || NONE_OPTION);
      const selectedResource = resources.find(
        (resource) => normalizeId(resource.id) === selectedId,
      );

      updateConnectionConfiguration<ConfluenceConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          resource: selectedId === NONE_OPTION ? undefined : selectedResource,
          spaces: [],
        }),
      );
    },
    onSelectSpaces: (spaceIds: string[]) => {
      const selected = spaces.filter((space) =>
        spaceIds.includes(normalizeId(space.id)),
      );

      updateConnectionConfiguration<ConfluenceConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          spaces: selected,
        }),
      );
    },
    onToggleAssignedToMe: (value: boolean) => {
      updateConnectionConfiguration<ConfluenceConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          assignedToMe: value,
        }),
      );
    },
  };
};
